import React, { useEffect, useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./editBrandsLeasing.css";
import { updateBrandsLeasing, viewBrandsLeasing } from "../../Redux/Actions/brandsLeasingActions";
import { getProjectList } from "../../Redux/Actions/projectActions";
import AddImage from "../../Global Functions/addImage";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

const EditBrandsLeasing = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { loading = false, brandsLeasingView = null } = useSelector(
        (state) => state.brandsLeasing || {}
    );
    const { projects = [], selectedBuilderId: builderId } = useSelector(
        (state) => state.projects || {}
    );
    // const { brandsLeasing = null } = useSelector((state) => state.brandsLeasing || {});

    const [activeSection, setActiveSection] = useState(0);
    const [imagePreview, setImagePreview] = useState("");
    const fileInputRef = useRef(null);

    const [brandsLeasingData, setBrandsLeasingData] = useState({
        project_id: "",
        name: "",
        image: "",
        lease_rent: "",
        starting_price: "",
        currency: "",
        type: "",
        note: "",
        locable: false,
    });

    const sections = ["Basic Info", "Lease Details", "Pricing"];

    // Fetch projects and existing brandsLeasing data
    useEffect(() => {
        const payload = {
            builder_id: builderId,
            status: ["approved", "published", "unpublished", "sold", "requested"],
            page: 1,
            limit: 1000,
            is_deleted: false,
        };

        dispatch(getProjectList(payload)).catch(() => {
            toast.error("Failed to fetch projects");
        });

        const viewPayload = { brand_leasing_id: id };
        if (id) {
            dispatch(viewBrandsLeasing(viewPayload)).catch(() => {
                toast.error("Failed to fetch brands leasing");
            });
        }
    }, [dispatch, builderId, id]);

    useEffect(() => {
        if (brandsLeasingView) {
            const data = brandsLeasingView?.data || brandsLeasingView;

            setBrandsLeasingData({
                project_id: data.project_id._id || "",
                name: data.name || "",
                image: data.image || "",
                lease_rent: data.lease_rent || "",
                starting_price: data.starting_price || "",
                currency: data.currency || "",
                type: data.type || "",
                note: data.note || "",
                locable: data.locable || false,
            });

            setImagePreview(data.image || "");
        }
    }, [brandsLeasingView]);

    const handleAddChange = (event) => {
        const { name, value, type, checked } = event.target;
        setBrandsLeasingData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            toast.loading("Uploading image...", { id: "image-upload" });
            const imageUrl = await AddImage(file);
            toast.dismiss("image-upload");

            if (imageUrl) {
                setImagePreview(imageUrl);
                setBrandsLeasingData((prev) => ({
                    ...prev,
                    image: imageUrl,
                }));
            }
        } catch (error) {
            toast.dismiss("image-upload");
            toast.error("Failed to upload image");
        }
    };

    const handleNextSection = () => {
        if (activeSection === 0) {
            if (!brandsLeasingData.project_id) {
                toast.error("Project is required");
                return;
            }
            if (!brandsLeasingData.name.trim()) {
                toast.error("Name is required");
                return;
            }
            if (!brandsLeasingData.image) {
                toast.error("Image is required");
                return;
            }
        }

        if (activeSection === 1) {
            if (!brandsLeasingData.lease_rent.trim()) {
                toast.error("Lease rent is required");
                return;
            }
            if (!brandsLeasingData.type.trim()) {
                toast.error("Type is required");
                return;
            }
        }

        const next = Math.min(sections.length - 1, activeSection + 1);
        setActiveSection(next);
    };

    const handleReset = () => {
        setBrandsLeasingData({
            project_id: "",
            name: "",
            image: "",
            lease_rent: "",
            starting_price: "",
            currency: "",
            type: "",
            note: "",
            locable: false,
        });
        setImagePreview("");
        setActiveSection(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveImage = () => {
        setImagePreview("");
        setBrandsLeasingData((prev) => ({
            ...prev,
            image: "",
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const sortedProjects = Array.isArray(projects)
        ? [...projects].sort((a, b) =>
            (a.project_name || a.name || "").localeCompare(
                b.project_name || b.name || "",
                undefined,
                { sensitivity: "base" }
            )
        )
        : [];

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!brandsLeasingData.project_id) return toast.error("Project is required");
        if (!brandsLeasingData.name.trim()) return toast.error("Name is required");
        if (!brandsLeasingData.image) return toast.error("Image is required");
        if (!brandsLeasingData.lease_rent.trim()) return toast.error("Lease rent is required");
        if (!brandsLeasingData.type.trim()) return toast.error("Type is required");
        if (!brandsLeasingData.starting_price) return toast.error("Starting price is required");
        if (!brandsLeasingData.currency) return toast.error("Currency is required");

        try {
            const response = await dispatch(updateBrandsLeasing(id, brandsLeasingData));
            if (response?.statusCode === 200) {
                // toast.success("Brands leasing updated successfully!");
                navigate("/brands_leasing");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to update brands leasing");
        }
    };

    return (
        <MainLayout>
            <div className="ebl-wrapper">
                <div className="ebl-header">
                    <div className="ebl-header-left">
                        <div className="ebl-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 21h18" />
                                <path d="M5 21V7l8-4 6 4v14" />
                                <path d="M9 9h.01" />
                                <path d="M9 13h.01" />
                                <path d="M9 17h.01" />
                                <path d="M13 9h.01" />
                                <path d="M13 13h.01" />
                                <path d="M13 17h.01" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="ebl-title">Edit Brands Leasing</h1>
                            <p className="ebl-subtitle">Update the brands leasing details</p>
                        </div>
                    </div>
                    <Link to="/brands_leasing" className="ebl-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 21h18" />
                            <path d="M5 21V7l8-4 6 4v14" />
                        </svg>
                        View Brands Leasing
                    </Link>
                </div>

                <div className="ebl-nav">
                    {sections.map((sec, i) => (
                        <button
                            key={i}
                            className={`ebl-nav-pill ${activeSection === i ? "ebl-nav-pill--active" : ""}`}
                            onClick={() => setActiveSection(i)}
                            type="button"
                            disabled={loading}
                        >
                            <span className="ebl-nav-num">{String(i + 1).padStart(2, "0")}</span>
                            {sec}
                        </button>
                    ))}
                </div>

                <form className="ebl-form">
                    <div className={`ebl-section ${activeSection === 0 ? "ebl-section--visible" : ""}`}>
                        <div className="ebl-section-header">
                            <span className="ebl-section-badge">01</span>
                            <h2 className="ebl-section-title">Basic Information</h2>
                        </div>

                        <div className="ebl-grid ebl-grid-2">
                            <div className="ebl-field">
                                <label className="ebl-label">Select Project <span className="ebl-req">*</span></label>
                                <select
                                    className="ebl-select"
                                    name="project_id"
                                    value={brandsLeasingData.project_id}
                                    onChange={handleAddChange}
                                    disabled={loading}
                                >
                                    <option value="">Select project</option>
                                    {sortedProjects.map((project) => (
                                        <option key={project._id || project.id} value={project._id || project.id}>
                                            {project.project_name || project.name || "N/A"}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="ebl-field">
                                <label className="ebl-label">Name <span className="ebl-req">*</span></label>
                                <input
                                    className="ebl-input"
                                    type="text"
                                    name="name"
                                    value={brandsLeasingData.name}
                                    onChange={handleAddChange}
                                    placeholder="Enter brand name"
                                    disabled={loading}
                                />
                            </div>

                            <div className="ebl-field ebl-field--full">
                                <label className="ebl-label">Image <span className="ebl-req">*</span></label>
                                <input
                                    ref={fileInputRef}
                                    className="ebl-input ebl-file"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    disabled={loading}
                                />
                            </div>

                            {imagePreview && (
                                <div className="ebl-field ebl-field--full">
                                    <div className="ebl-image-box">
                                        <img src={imagePreview} alt="Preview" className="ebl-image-preview" />
                                        <button
                                            type="button"
                                            className="ebl-remove-image"
                                            onClick={handleRemoveImage}
                                            disabled={loading}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`ebl-section ${activeSection === 1 ? "ebl-section--visible" : ""}`}>
                        <div className="ebl-section-header">
                            <span className="ebl-section-badge">02</span>
                            <h2 className="ebl-section-title">Lease Details</h2>
                        </div>

                        <div className="ebl-grid ebl-grid-2">
                            <div className="ebl-field">
                                <label className="ebl-label">Lease Rent <span className="ebl-req">*</span></label>
                                <input
                                    className="ebl-input"
                                    type="text"
                                    name="lease_rent"
                                    value={brandsLeasingData.lease_rent}
                                    onChange={handleAddChange}
                                    placeholder="Enter lease rent"
                                    disabled={loading}
                                />
                            </div>

                            <div className="ebl-field">
                                <label className="ebl-label">Type <span className="ebl-req">*</span></label>
                                <input
                                    className="ebl-input"
                                    type="text"
                                    name="type"
                                    value={brandsLeasingData.type}
                                    onChange={handleAddChange}
                                    placeholder="Enter type"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={`ebl-section ${activeSection === 2 ? "ebl-section--visible" : ""}`}>
                        <div className="ebl-section-header">
                            <span className="ebl-section-badge">03</span>
                            <h2 className="ebl-section-title">Pricing</h2>
                        </div>

                        <div className="ebl-grid ebl-grid-2">
                            <div className="ebl-field">
                                <label className="ebl-label">Starting Price <span className="ebl-req">*</span></label>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <select
                                        className="ebl-select"
                                        name="currency"
                                        value={brandsLeasingData.currency}
                                        onChange={handleAddChange}
                                        disabled={loading}
                                        style={{ width: "110px", flexShrink: 0 }}
                                    >
                                        <option value="">Currency</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="GBP">GBP (£)</option>
                                        <option value="INR">INR (₹)</option>
                                        <option value="AED">AED (د.إ)</option>
                                        <option value="SAR">SAR (﷼)</option>
                                    </select>
                                    <input
                                        className="ebl-input"
                                        type="number"
                                        name="starting_price"
                                        value={brandsLeasingData.starting_price}
                                        onChange={handleAddChange}
                                        placeholder="Enter starting price"
                                        disabled={loading}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>

                            <div className="ebl-field">
                                <label className="ebl-label">lockable <span className="ebl-optional">(Optional)</span></label>
                                <label className="ebl-check-wrap">
                                    <input
                                        type="checkbox"
                                        name="locable"
                                        checked={brandsLeasingData.locable}
                                        onChange={handleAddChange}
                                        disabled={loading}
                                    />
                                    <span>Is lockable</span>
                                </label>
                            </div>
                            <div className="ebl-field">
                                <label className="ebl-label">Note</label>
                                <input
                                    className="ebl-input"
                                    type="text"
                                    name="note"
                                    value={brandsLeasingData.note}
                                    onChange={handleAddChange}
                                    placeholder="Enter note"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ebl-footer">
                        <div className="ebl-footer-nav">
                            <button
                                type="button"
                                className="ebl-btn ebl-btn--ghost"
                                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                                disabled={activeSection === 0 || loading}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                                Back
                            </button>

                            {activeSection < sections.length - 1 ? (
                                <button
                                    type="button"
                                    className="ebl-btn ebl-btn--primary"
                                    onClick={handleNextSection}
                                    disabled={loading}
                                >
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="ebl-btn ebl-btn--submit"
                                    disabled={loading}
                                >
                                    {loading ? "Updating..." : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Update Brands Leasing
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        <Link to="/brands_leasing" className="ebl-btn ebl-btn--cancel" onClick={handleReset}>
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default EditBrandsLeasing;